import React, { useState, useEffect } from "react";
import { UserProfile, IActionHandler, WebsocketCryptoMessage, ActionHandlerFunction } from "@preveil-api";
import {
  Account, AccountWSSteps, isSameUser, Message, useAppSelector, useAppDispatch, MessageHandlerDisplayType, AdminErrorMessages, DataExportRequestStatus, AdminUIActionTypes,
  ExportRequest, ConfigKeys
} from "src/common";
import { Loading } from "src/components";
import { ActiveConnection, ApproversPanel, AwaitConnection, ErrorConnection } from ".";
import { uiActions, websocketActions, WebsocketStatus, getConfigValue } from "src/store";
import { RootState } from "src/store/configureStore";

type AllProps = {
  export_request: ExportRequest;
  step: keyof typeof DataExportRequestStatus;
  current_account: Account;
  approvers: UserProfile[];
  approved_users: UserProfile[];
  required_approvers?: number;
  handleAction: ActionHandlerFunction;
}

// Description: Component for the approval step of the data export process - requesting approvals. (uses websocket)
function ApproveStepComponent(props: AllProps) {
  const { export_request, step, current_account, approvers, approved_users, required_approvers, handleAction } = props;
  const dispatch = useAppDispatch();
  const crypto_websocket_server = getConfigValue(ConfigKeys.crypto_websocket_server);
  const status = useAppSelector((state: RootState) => state.websocket.status);
  const errors = useAppSelector((state: RootState) => state.websocket.errors);
  const message = useAppSelector((state: RootState) => state.websocket.message as WebsocketCryptoMessage);
  const [pin, setPin] = useState<string>("");
  const [user_to_approve, setUserToApprove] = useState<UserProfile>();
  const [ws_step, setWsStep] = useState<number>(AccountWSSteps.DISCONNECTED);
  const requester_found = approved_users.length === 0 && approvers.filter((user) => (isSameUser(user.address, current_account.user_id)));
  const current_users_approved = Array.isArray(requester_found) && requester_found.length > 0 ? [...approved_users, ...requester_found] : [...approved_users];

  // Description: On leaving component - close the websocket connection and clean up
  useEffect(() => {
    return () => {
      destroyConnection();
    };
  }, []);

  // Description: Handle errors
  useEffect(() => {
    !!errors &&
      dispatch(uiActions.handleRequestErrors(new Message(AdminErrorMessages.default, MessageHandlerDisplayType.logger), errors));
  }, [errors]);

  // Description: set ws_step to DISCONNECTED when the export request is not started
  useEffect(() => {
    if (step === DataExportRequestStatus.not_started && ws_step !== AccountWSSteps.DISCONNECTED) {
      setWsStep(AccountWSSteps.DISCONNECTED);
    }
  }, [ws_step, step]);

  // Description: Respond to changes in message and websocket status
  useEffect(() => {
    const is_error = status === WebsocketStatus.error || message?.status === WebsocketStatus.error;
    let next = is_error ? AccountWSSteps.ERROR : !!message ? ws_step + 1 : ws_step;

    if (!!message?.pin) {
      setPin(message.pin);
      next = AccountWSSteps.GET_CONNECTION_ESTABLISHED;
    }
    setWsStep(next);

    if (!!message?.status && message.status === WebsocketStatus.key_transfer_complete) {
      handleAction({ actionType: AdminUIActionTypes.GetCurrentUserExportRequest });
      setWsStep(AccountWSSteps.DISCONNECTED);
    }

  }, [message, status]);

  // Description: Initialize the websocket connection
  function initWebsocketConnection(user_id: string) {
    // This is the signature we are using in V1.
    const options = {
      signature: "dummy"
    };
    const host = `${crypto_websocket_server}/users/${encodeURIComponent(current_account.user_id)}/connect/${encodeURIComponent(user_id)}`;
    dispatch(websocketActions.wsConnect({ host, init: { action: "pre_init", request: { ...export_request, ...options } } }));
  }

  // Description: Close the websocket connection and clean up
  function destroyConnection() {
    dispatch(websocketActions.wsDisconnect());
    dispatch(websocketActions.destroyWebsocket());
    setWsStep(AccountWSSteps.DISCONNECTED);
  }

  // Description: Handle the actions for the data export approval process
  const DataExportApproveRequest = {
    handleStart: (params: { subsumeUser: string }) => {
      const { subsumeUser: user_id } = params;
      const approver = approvers.find((user: UserProfile) => (isSameUser(user.address, user_id)));
      if (!!approver) {
        setUserToApprove(approver);
        initWebsocketConnection(user_id);
      } else {
        dispatch(uiActions.handleRequestErrors(new Message(AdminErrorMessages.default, MessageHandlerDisplayType.logger), errors));
      }
    },
    handleReset: () => {
      destroyConnection();
    }
  };

  function handlePageActions(action_obj: IActionHandler) {
    const callback = `handle${action_obj.actionType}`;
    if ((DataExportApproveRequest as any)[callback] instanceof Function) {
      (DataExportApproveRequest as any)[callback](action_obj.params);
    }
  }

  function uiPanels() {
    switch (ws_step) {
      case AccountWSSteps.DISCONNECTED:
      case AccountWSSteps.GET_KEY_TRANSFER_COMPLETE:
        return <ApproversPanel approvers={approvers} current_users_approved={current_users_approved} handleAction={handlePageActions} required_approvers={required_approvers} />;
      case AccountWSSteps.INITIALIZE_CONNECTION:
      case AccountWSSteps.GET_ACCEPTED_INIT_OK:
        return !!user_to_approve && <AwaitConnection approver={user_to_approve} handleAction={handlePageActions} />;
      case AccountWSSteps.GET_CONNECTION_ESTABLISHED:
        return !!pin ? <ActiveConnection pin={pin} /> : <Loading />;
      case AccountWSSteps.ERROR:
        return !!user_to_approve && <ErrorConnection handleAction={handlePageActions} approver={user_to_approve} />;
      default:
        return null;
    }
  };

  return <div className="export-container">{uiPanels()}</div>;
}

export default React.memo(ApproveStepComponent);